import React, { useState, useEffect } from "react";
import { useOverview } from "../../hooks";
import { LogoStockal, VisaLogo } from "./../../Public/assets";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "./../../Components/ui/accordion";
import { TransactionItem } from "../../Components/TransactionDetails/TransactionDetails";
import { SkeletonBox } from "./../../Components/skeleton";
import {
  setTransactionLimits,
  getTransactionLimits,
  changeCardStatus,
} from "../../api";

const ManageCard = ({
  queryClient,
  onCloseDrawer,
  setIsCardFrozen,
  setHasLimitErrors,
  setIsSavingLimits: setIsSavingLimitsParent,
  onSave,
}) => {
  const {
    useCardDetails,
    useTransactionDetails,
    setActiveTab,
    setTransactionCurrencyFilter,
  } = useOverview();
  const { data: cardDetailsResponse, isLoading: isLoadingCard } =
    useCardDetails();
  const { data: transactionDataList, isLoading: isLoadingTransactions } =
    useTransactionDetails({ type: "card" });

  const cardData = cardDetailsResponse?.data;

  const [isCardActive, setIsCardActive] = useState(false);
  const [perTransactionLimit, setPerTransactionLimit] = useState("");
  const [dailyTransactionLimit, setDailyTransactionLimit] = useState("");
  const [isFlipped, setIsFlipped] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isSavingLimits, setIsSavingLimits] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [showLimitConfirmation, setShowLimitConfirmation] = useState(false);
  const [maximumLimits, setMaximumLimits] = useState({
    perTransaction: null,
    daily: null,
  });
  const [perTransactionError, setPerTransactionError] = useState("");
  const [dailyTransactionError, setDailyTransactionError] = useState("");
  // Store current/original limits separately (won't change during input)
  const [currentLimits, setCurrentLimits] = useState({
    perTransaction: null,
    daily: null,
  });

  // Sync card active state with API data
  useEffect(() => {
    if (cardData?.status) {
      // Card is active if status is "ACTIVE" or "APPROVED"
      const isActive =
        cardData.status === "ACTIVE" || cardData.status === "APPROVED";
      setIsCardActive(isActive);

      // Update parent component with frozen state
      if (setIsCardFrozen) {
        setIsCardFrozen(!isActive);
      }
    }
  }, [cardData?.status, setIsCardFrozen]);

  // Fetch and populate transaction limits
  useEffect(() => {
    const fetchTransactionLimits = async () => {
      try {
        const response = await getTransactionLimits();

        if (response?.code === 200 || response?.status === 200) {
          const data = response?.data;

          // Set current control limits
          if (data?.controlLimits) {
            const perTransactionLimit = data.controlLimits.find(
              (limit) => limit.type === "PER_TRANSACTION"
            );
            const dailyLimit = data.controlLimits.find(
              (limit) => limit.type === "DAILY"
            );

            // Store current limits (won't change during input)
            setCurrentLimits({
              perTransaction: perTransactionLimit?.amount || null,
              daily: dailyLimit?.amount || null,
            });

            if (perTransactionLimit?.amount) {
              setPerTransactionLimit(perTransactionLimit.amount.toString());
            }

            if (dailyLimit?.amount) {
              setDailyTransactionLimit(dailyLimit.amount.toString());
            }
          }

          // Set maximum limits for validation
          if (data?.maximumLimits) {
            const maxPerTransaction = data.maximumLimits.find(
              (limit) => limit.type === "PER_TRANSACTION"
            );
            const maxDaily = data.maximumLimits.find(
              (limit) => limit.type === "DAILY"
            );

            setMaximumLimits({
              perTransaction: maxPerTransaction?.amount || null,
              daily: maxDaily?.amount || null,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching transaction limits:", error);
        // Don't show error to user, just fail silently and leave fields empty
      }
    };

    // Fetch limits on component mount (when drawer opens with ManageCard)
    fetchTransactionLimits();
  }, []); // Empty dependency array ensures this runs once when component mounts

  // Format card number for display
  const formatCardNumber = (number) => {
    if (!number) return "XXXX XXXX XXXX ";
    // If already masked (contains *), just return it
    if (number.includes("*")) return number.replace(/\*/g, "X");
    // Otherwise mask it
    const cleaned = number.replace(/\s/g, "");
    return `XXXX XXXX XXXX ${cleaned.slice(-4)}`;
  };

  // Handle flip to unlock
  // Flip here
  // const handleFlip = () => {
  //   if (isFlipped) return;
  //   setIsFlipped(true);
  // };

  const handleDateSubmit = () => {
    if (dateOfBirth.length >= 8) {
      setIsUnlocked(true);
      console.log("DOB submitted:", dateOfBirth);
      // Flip back to front
      setTimeout(() => {
        setIsFlipped(false);
      }, 600);
    }
  };

  const handleCancelFlip = () => {
    setDateOfBirth("");
    setIsFlipped(false);
  };

  // Handle saving transaction limits - exposed to parent
  const handleSaveTransactionLimits = React.useCallback(async () => {
    // Check for existing errors
    if (perTransactionError || dailyTransactionError) {
      return false;
    }

    // Validate inputs
    if (!perTransactionLimit && !dailyTransactionLimit) {
      return false;
    }

    const controlLimits = [];

    if (perTransactionLimit && perTransactionLimit.trim() !== "") {
      const amount = parseFloat(perTransactionLimit);
      if (isNaN(amount) || amount <= 0) {
        setPerTransactionError("Please enter a valid per transaction limit.");
        return false;
      }

      // Validate against maximum limit
      if (
        maximumLimits.perTransaction &&
        amount > maximumLimits.perTransaction
      ) {
        setPerTransactionError(
          `Per transaction limit cannot exceed ${maximumLimits.perTransaction}`
        );
        return false;
      }

      controlLimits.push({
        amount: amount,
        type: "PER_TRANSACTION",
      });
    }

    if (dailyTransactionLimit && dailyTransactionLimit.trim() !== "") {
      const amount = parseFloat(dailyTransactionLimit);
      if (isNaN(amount) || amount <= 0) {
        setDailyTransactionError(
          "Please enter a valid daily transaction limit."
        );
        return false;
      }

      // Validate against maximum limit
      if (maximumLimits.daily && amount > maximumLimits.daily) {
        setDailyTransactionError(
          `Daily transaction limit cannot exceed ${maximumLimits.daily}`
        );
        return false;
      }

      controlLimits.push({
        amount: amount,
        type: "DAILY",
      });
    }

    if (controlLimits.length === 0) {
      return false;
    }

    setIsSavingLimits(true);
    if (setIsSavingLimitsParent) {
      setIsSavingLimitsParent(true);
    }

    try {
      const response = await setTransactionLimits(controlLimits);

      if (
        response?.code === 200 ||
        response?.status === 200 ||
        response?.success
      ) {
        // Update current limits to reflect the saved values
        const newCurrentLimits = { ...currentLimits };

        controlLimits.forEach((limit) => {
          if (limit.type === "PER_TRANSACTION") {
            newCurrentLimits.perTransaction = limit.amount;
          } else if (limit.type === "DAILY") {
            newCurrentLimits.daily = limit.amount;
          }
        });

        setCurrentLimits(newCurrentLimits);

        // Show confirmation message and auto-dismiss after 10 seconds
        setIsSavingLimits(false);
        if (setIsSavingLimitsParent) {
          setIsSavingLimitsParent(false);
        }
        setShowLimitConfirmation(true);

        setTimeout(() => {
          setShowLimitConfirmation(false);
        }, 10000);

        return true;
      } else {
        // API error - could show in UI if needed
        console.error(
          "Failed to update transaction limits:",
          response?.message
        );
        setIsSavingLimits(false);
        if (setIsSavingLimitsParent) {
          setIsSavingLimitsParent(false);
        }
        return false;
      }
    } catch (error) {
      console.error("Error updating transaction limits:", error);
      setIsSavingLimits(false);
      if (setIsSavingLimitsParent) {
        setIsSavingLimitsParent(false);
      }
      return false;
    }
  }, [
    perTransactionLimit,
    dailyTransactionLimit,
    maximumLimits,
    perTransactionError,
    dailyTransactionError,
    setIsSavingLimitsParent,
    currentLimits,
  ]);

  // Notify parent about error state changes
  React.useEffect(() => {
    if (setHasLimitErrors) {
      setHasLimitErrors(!!(perTransactionError || dailyTransactionError));
    }
  }, [perTransactionError, dailyTransactionError, setHasLimitErrors]);

  // Expose the save handler via onSave callback for parent to call
  React.useEffect(() => {
    if (onSave) {
      // This is still using window temporarily for backward compatibility
      window.saveTransactionLimits = handleSaveTransactionLimits;
      return () => {
        delete window.saveTransactionLimits;
      };
    }
  }, [handleSaveTransactionLimits, onSave]);

  // Handle per transaction limit change with validation
  const handlePerTransactionLimitChange = (e) => {
    const value = e.target.value;
    setPerTransactionLimit(value);

    if (value) {
      const amount = parseFloat(value);

      // Check for negative or zero values
      if (!isNaN(amount) && amount <= 0) {
        setPerTransactionError(
          "Per Transaction limit must be greater than zero"
        );
      } else if (
        !isNaN(amount) &&
        maximumLimits.perTransaction &&
        amount > maximumLimits.perTransaction
      ) {
        setPerTransactionError(
          `Per Transaction maximum limit is USD ${maximumLimits.perTransaction.toLocaleString()}`
        );
      } else if (dailyTransactionLimit && !isNaN(amount)) {
        const dailyAmount = parseFloat(dailyTransactionLimit);
        if (!isNaN(dailyAmount) && amount > dailyAmount) {
          setPerTransactionError(
            "Per Transaction limit cannot exceed Daily Transaction limit"
          );
        } else {
          setPerTransactionError("");
          // Clear daily error if it was about per transaction being higher
          if (dailyTransactionError.includes("cannot be less")) {
            setDailyTransactionError("");
          }
        }
      } else {
        setPerTransactionError("");
      }
    } else {
      setPerTransactionError("");
    }
  };

  // Handle daily transaction limit change with validation
  const handleDailyTransactionLimitChange = (e) => {
    const value = e.target.value;
    setDailyTransactionLimit(value);

    if (value) {
      const amount = parseFloat(value);

      // Check for negative or zero values
      if (!isNaN(amount) && amount <= 0) {
        setDailyTransactionError(
          "Daily Transaction limit must be greater than zero"
        );
      } else if (
        !isNaN(amount) &&
        maximumLimits.daily &&
        amount > maximumLimits.daily
      ) {
        setDailyTransactionError(
          `Daily Transaction maximum limit is USD ${maximumLimits.daily.toLocaleString()}`
        );
      } else if (perTransactionLimit && !isNaN(amount)) {
        const perTransAmount = parseFloat(perTransactionLimit);
        if (!isNaN(perTransAmount) && amount < perTransAmount) {
          setDailyTransactionError(
            "Daily Transaction limit cannot be less than Per Transaction limit"
          );
        } else {
          setDailyTransactionError("");
          // Clear per transaction error if it was about exceeding daily
          if (perTransactionError.includes("cannot exceed Daily")) {
            setPerTransactionError("");
          }
        }
      } else {
        setDailyTransactionError("");
      }
    } else {
      setDailyTransactionError("");
    }
  };

  // Handle card toggle click - show confirmation dialog
  const handleCardToggle = async () => {
    setShowConfirmDialog(true);
  };

  // Handle confirmation of card status change
  const handleConfirmStatusChange = async () => {
    setIsChangingStatus(true);
    const newStatus = !isCardActive;

    try {
      const status = newStatus ? "ACTIVE" : "INACTIVE";
      await changeCardStatus(status);

      // Update local state immediately for better UX
      setIsCardActive(newStatus);

      // Update parent component with frozen state
      if (setIsCardFrozen) {
        setIsCardFrozen(!newStatus);
      }

      setShowConfirmDialog(false);
      setIsChangingStatus(false);

      // Close drawer immediately
      // if (onCloseDrawer) {
      //   onCloseDrawer();
      // }

      // Invalidate and refetch cache after 10 seconds
      // During this time, the home screen card will show a loading state
      // setTimeout(async () => {
      await queryClient.invalidateQueries({
        queryKey: ["cardDetails"],
      });

      // // Force immediate refetch to ensure all components get the updated status
      // await queryClient.refetchQueries({
      //   queryKey: ["cardDetails"],
      // });
      // }, 10000); // 10 second delay before invalidation
    } catch (error) {
      console.error("Failed to change card status:", error);
      setIsChangingStatus(false);
      setShowConfirmDialog(false);
    }
  };

  // Handle cancel confirmation
  const handleCancelConfirmation = () => {
    setShowConfirmDialog(false);
  };

  if (isLoadingCard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-b-2 rounded-full animate-spin border-blueish"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 py-6">
      {/* Card Display Section */}
      <div className="w-full px-2">
        <div className="relative w-full" style={{ perspective: "1000px" }}>
          <div
            className="relative w-full transition-all duration-[600ms] ease-in-out"
            style={{
              transformStyle: "preserve-3d",
              transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
            }}
          >
            {/* Card Front */}
            <div
              className="w-full"
              style={{
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                pointerEvents: isFlipped ? "none" : "auto",
              }}
            >
              <div
                className={`relative w-full rounded-2xl shadow-lg overflow-hidden border-[1.5px] ${
                  !isCardActive
                    ? "cursor-pointer"
                    : !isUnlocked && isCardActive
                    ? "cursor-pointer"
                    : ""
                }`}
                style={{
                  background:
                    "linear-gradient(135deg, #FFFFFF 0%, #F5F7FA 100%)",
                }}
                onClick={
                  !isCardActive
                    ? handleCardToggle
                    : !isUnlocked && isCardActive
                    ? undefined
                    : undefined
                }
                // handleFlip
              >
                {/* Frozen State Overlay */}
                {!isCardActive && (
                  <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-lg text-blueish font-geomanistMedium">
                        Tap to Unfreeze card
                      </div>
                    </div>
                  </div>
                )}

                {/* Card Header with Toggle */}
                <div className="relative z-20 flex items-start justify-between p-6 pb-0">
                  {/* Bank Logo */}
                  <div className="flex items-center justify-center w-10 h-10">
                    <img src={LogoStockal} alt="" />
                  </div>

                  {/* Toggle Switch */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCardToggle();
                      }}
                      disabled={isChangingStatus}
                      className={`relative inline-flex h-7 w-16 items-center justify-between rounded-full transition-colors px-1 ${
                        isCardActive ? "bg-green-500" : "bg-red-500"
                      } ${
                        isChangingStatus ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      {isChangingStatus ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <svg
                            className="w-4 h-4 text-white animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        </div>
                      ) : (
                        <>
                          <span
                            className={`text-[12px] font-geomanistBold ml-2 text-white z-10 transition-opacity ${
                              isCardActive ? "opacity-100" : "opacity-0"
                            }`}
                          >
                            ON
                          </span>
                          <span
                            className={`absolute inset-0 flex items-center transition-all ${
                              isCardActive
                                ? "justify-end pr-1"
                                : "justify-start pl-1"
                            }`}
                          >
                            <span className="inline-block w-5 h-5 bg-white rounded-full shadow-md" />
                          </span>
                          <span
                            className={`text-[10px] mr-2 font-geomanistMedium text-white z-10 transition-opacity ${
                              !isCardActive ? "opacity-100" : "opacity-0"
                            }`}
                          >
                            OFF
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Card Number */}
                <div className="px-6 pt-4">
                  <div className="font-geomanistBold font-normal text-[16px] leading-[24px] tracking-[0px] text-[#3C404A]">
                    {isUnlocked
                      ? cardData?.cardNo
                      : formatCardNumber(cardData?.cardNo)}
                  </div>
                </div>

                {/* CVV and Expiry */}
                <div className="flex justify-between items-center px-6 py-1 pb-[20px]">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-geomanistBold font-normal text-[13px] leading-[100%] tracking-[0px] text-[#767C8F] ">
                      CVV
                    </div>

                    <div className="font-geomanist font-normal text-[13px] leading-[100%] tracking-[0px] text-[#3C404A]">
                      {isUnlocked ? cardData?.cvv : "XXX"}
                    </div>
                  </div>
                  <div className="flex gap-2 text-right">
                    <div className="font-geomanist font-normal text-[14px] leading-[20px] tracking-[0px] text-[#767C8F] mb-1">
                      Expiry
                    </div>

                    <div className="font-geomanist font-normal text-[14px] leading-[20px] tracking-[0px] text-[#3C404A]">
                      {isUnlocked ? cardData?.expiry : "XX/XX"}
                    </div>
                  </div>
                </div>

                {/* Card Footer - Blue Bar */}
                <div className="flex items-center justify-between px-6 py-4 bg-blueish ">
                  <div className="text-base text-white font-geomanistMedium">
                    {cardData?.nameOnCard}
                  </div>
                  <div className="text-lg italic text-white font-geomanistBold">
                    <img src={VisaLogo} alt="" />
                  </div>
                </div>
              </div>
            </div>

            {/* Card Back - DOB Input */}
            <div
              className="absolute inset-0 w-full"
              style={{
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                transform: "rotateY(180deg)",
                pointerEvents: isFlipped ? "auto" : "none",
              }}
            >
              <div
                className="relative w-full h-full rounded-2xl shadow-lg p-6 flex flex-col items-center justify-center bg-white border-[1.5px]"
                style={{
                  filter: "drop-shadow(0px 8px 16px rgba(0, 0, 0, 0.08))",
                }}
              >
                {/* Close Icon */}
                <button
                  onClick={handleCancelFlip}
                  className="absolute flex items-center justify-center w-8 h-8 transition-colors bg-gray-100 rounded-full top-4 right-4 hover:bg-gray-200"
                >
                  <span className="text-2xl leading-none text-gray-600">×</span>
                </button>

                <div className="flex flex-col items-center justify-center w-full max-w-sm gap-6 px-4">
                  <p className="font-geomanistRegular text-sm text-gray-700 text-center leading-[140%]">
                    Please enter your date of birth to view your debit card
                    details.
                  </p>

                  <div className="w-full">
                    <input
                      type="text"
                      className="w-full px-4 py-3 text-sm text-gray-900 placeholder-gray-400 bg-white border border-gray-300 rounded-lg font-geomanistRegular focus:outline-none focus:ring-2 focus:ring-blueish focus:border-transparent"
                      placeholder="DD/MM/YYYY"
                      value={dateOfBirth}
                      onChange={(e) => setDateOfBirth(e.target.value)}
                      maxLength={10}
                    />
                  </div>

                  <button
                    onClick={handleDateSubmit}
                    className="px-8 py-3 text-sm text-white transition-colors rounded-lg bg-blueish hover:bg-blue-700 font-geomanistRegular"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Set Transaction Limits Section */}
      <div className="w-full px-2">
        <div
          className={`bg-white rounded-[10px] overflow-hidden mx-auto relative ${
            !isCardActive ? "pointer-events-none" : ""
          }`}
          style={{
            boxShadow: "0px 4px 194px 0px rgba(0, 0, 0, 0.23)",
          }}
        >
          {/* Blur overlay when card is frozen */}
          {!isCardActive && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center">
              <div className="px-4 text-center">
                {/* <p className="text-sm text-gray-600 font-geomanistMedium">
                  Unfreeze card to change transaction limits
                </p> */}
              </div>
            </div>
          )}
          <Accordion type="single" collapsible defaultValue="item-1">
            <AccordionItem value="item-1">
              <AccordionTrigger className="px-4 py-4 font-geomanistBold font-normal text-[16px] leading-[24px] tracking-[0px] text-[#3C404A]">
                Set Transaction Limits
              </AccordionTrigger>
              <AccordionContent className="">
                <div className="">
                  {/* Per Transaction Limit */}
                  <div className=" bg-[#EFF7FF] space-y-4 px-4 pt-4 ">
                    <label className="block font-geomanist font-normal text-[14px] leading-[100%] tracking-[0px] text-[#3C404A]">
                      Per Transaction Limit
                    </label>
                    <input
                      type="number"
                      placeholder="Enter Amount"
                      value={perTransactionLimit}
                      onChange={handlePerTransactionLimitChange}
                      // onWheel={handleNumberInputWheel}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          (e) => e.preventDefault(),
                          { passive: false }
                        )
                      }
                      onBlur={(e) =>
                        e.target.removeEventListener("wheel", (e) =>
                          e.preventDefault()
                        )
                      }
                      disabled={!isCardActive}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent font-geomanistRegular text-sm text-gray-900 placeholder-gray-400 ${
                        perTransactionError
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:ring-blueish"
                      } ${
                        !isCardActive ? "cursor-not-allowed opacity-60" : ""
                      }`}
                    />
                    {perTransactionError && (
                      <p className="text-sm text-red-600 font-geomanistRegular">
                        {perTransactionError}
                      </p>
                    )}
                    {!perTransactionError && maximumLimits.perTransaction && (
                      <p className="text-xs text-gray-500 font-geomanistRegular">
                        Maximum limit: $
                        {maximumLimits.perTransaction.toLocaleString()}
                      </p>
                    )}
                    <div className="px-4 py-3 rounded-lg bg-blue-50">
                      <p className="font-geomanistBold font-normal text-[14px] leading-[100%] tracking-[0px] text-center  text-[#3C404A]">
                        Current Limit{" "}
                        {cardData?.limits?.perTransactionLimit?.currency ||
                          "USD"}{" "}
                        {currentLimits.perTransaction ||
                          cardData?.limits?.perTransactionLimit?.amount ||
                          500}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3"></div>

                  {/* Daily Transaction Limit */}
                  <div className=" bg-[#FCF1E7] space-y-4  pt-4 px-4">
                    <label className="block font-geomanist font-normal text-[14px] leading-[100%] tracking-[0px] text-[#3C404A]">
                      Daily Transaction Limit
                    </label>
                    <input
                      type="number"
                      placeholder="Enter Amount"
                      value={dailyTransactionLimit}
                      onChange={handleDailyTransactionLimitChange}
                      // onWheel={handleNumberInputWheel}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          (e) => e.preventDefault(),
                          { passive: false }
                        )
                      }
                      onBlur={(e) =>
                        e.target.removeEventListener("wheel", (e) =>
                          e.preventDefault()
                        )
                      }
                      disabled={!isCardActive}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent font-geomanistRegular text-sm text-gray-900 placeholder-gray-400 ${
                        dailyTransactionError
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:ring-blueish"
                      } ${
                        !isCardActive ? "cursor-not-allowed opacity-60" : ""
                      }`}
                    />
                    {dailyTransactionError && (
                      <p className="text-sm text-red-600 font-geomanistRegular">
                        {dailyTransactionError}
                      </p>
                    )}
                    {!dailyTransactionError && maximumLimits.daily && (
                      <p className="text-xs text-gray-500 font-geomanistRegular">
                        Maximum limit: ${maximumLimits.daily.toLocaleString()}
                      </p>
                    )}
                    <div className="bg-[#FCF1E7] px-4 py-3 rounded-lg">
                      <p className="font-geomanistBold font-normal text-[14px] leading-[100%] tracking-[0px] text-center  text-[#3C404A]">
                        Current Limit{" "}
                        {cardData?.limits?.dailyTransactionLimit?.currency ||
                          "USD"}{" "}
                        {currentLimits.daily ||
                          cardData?.limits?.dailyTransactionLimit?.amount ||
                          1000}
                      </p>
                    </div>
                  </div>
                  {showLimitConfirmation && (
                    <div className="mx-4 mt-4 mb-2">
                      <div className="px-4 py-3 border border-green-200 rounded-lg bg-green-50">
                        <p className="font-geomanistBold font-normal text-[14px] leading-[100%] tracking-[0px] text-center text-green-600">
                          Limits Updated Successfully
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* Recent Transactions Section */}
      <div className="w-full px-2">
        {isLoadingTransactions ? (
          <SkeletonBox />
        ) : (
          <div className="bg-white py-[26px] rounded-lg shadow-minShadow w-full font-geomanist text-sm leading-tight">
            <div className="flex items-center justify-between px-6 mb-3">
              <h2 className="font-geomanistBold font-normal text-[16px] leading-[24px] tracking-[0px] text-[#3C404A]">
                Recent Transactions
              </h2>

              <button
                onClick={() => {
                  if (onCloseDrawer) {
                    onCloseDrawer();
                  }
                  setTransactionCurrencyFilter("Debit Card");
                  setActiveTab("transactions");
                }}
                className="font-geomanistBold font-normal text-[16px] leading-[24px] tracking-[0px] underline decoration-solid decoration-[1px] decoration-[#0061D3] text-[#0061D3]"
              >
                View All
              </button>
            </div>
            <div className="space-y-2">
              {transactionDataList?.data?.transactions?.length > 0 ? (
                transactionDataList.data.transactions
                  // ?.filter(
                  //   (transaction) => transaction.transaction_type === "CARD_SPEND"
                  // )
                  .slice(0, 5)
                  .map((transaction, index) => (
                    <TransactionItem key={index} {...transaction} />
                  ))
              ) : (
                <p className="font-geomanistRegular text-base text-[#767C8F] text-center py-4">
                  No transactions found
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-md p-6 space-y-4 bg-white shadow-2xl rounded-xl">
            <h3 className="font-geomanistBold text-xl text-[#3C404A]">
              {isCardActive ? "Freeze Card?" : "Unfreeze Card?"}
            </h3>
            <p className="font-geomanistRegular text-base text-[#767C8F]">
              {isCardActive
                ? "Are you sure you want to freeze this card? You won't be able to make transactions until you unfreeze it."
                : "Are you sure you want to unfreeze this card? This will allow you to make transactions again."}
            </p>
            <div className="flex gap-3 pt-2">
              <button
                onClick={handleCancelConfirmation}
                disabled={isChangingStatus}
                className="flex-1 px-4 py-3 bg-gray-100 text-[#3C404A] rounded-lg hover:bg-gray-200 transition-colors font-geomanistMedium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmStatusChange}
                disabled={isChangingStatus}
                className="flex-1 px-4 py-3 text-white transition-colors rounded-lg bg-blueish hover:bg-blue-700 font-geomanistMedium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isChangingStatus ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="w-5 h-5 mr-2 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Processing...
                  </span>
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCard;
